import { useMutation } from '@apollo/client';

import { useCompleteFragment } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { useAppointmentContext } from './appointment.context';
import { appointmentLlmOutputTemplateFragment } from './operations';

const AppointmentTemplateFragment = graphql(`
  fragment AppointmentTemplate on Appointments {
    __typename
    id
    initialPromptTemplateId
    initialLlmOutputTemplateId
  }
`);

const updateAppointmentTemplateMutation = graphql(
  `
    mutation UpdateAppointmentTemplateMutation(
      $tenantId: uuid!
      $id: uuid!
      $initialPromptTemplateId: uuid
      $initialLlmOutputTemplateId: uuid
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: {
          initialPromptTemplateId: $initialPromptTemplateId
          initialLlmOutputTemplateId: $initialLlmOutputTemplateId
        }
      ) {
        __typename
        id
        initialPromptTemplateId
        initialLlmOutputTemplateId
        ...llmOutputTemplate
      }
    }
  `,
  [appointmentLlmOutputTemplateFragment],
);

const updateAppointmentPromptTemplateMutation = graphql(
  `
    mutation UpdateAppointmentPromptTemplateMutation(
      $tenantId: uuid!
      $id: uuid!
      $initialPromptTemplateId: uuid!
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: { initialPromptTemplateId: $initialPromptTemplateId }
      ) {
        __typename
        id
        initialPromptTemplateId
        ...llmOutputTemplate
      }
    }
  `,
  [appointmentLlmOutputTemplateFragment],
);

const updateAppointmentOutputTemplateMutation = graphql(
  `
    mutation UpdateAppointmentTemplateMutation(
      $tenantId: uuid!
      $id: uuid!
      $initialLlmOutputTemplateId: uuid!
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: { initialLlmOutputTemplateId: $initialLlmOutputTemplateId }
      ) {
        __typename
        id
        initialLlmOutputTemplateId
        ...llmOutputTemplate
      }
    }
  `,
  [appointmentLlmOutputTemplateFragment],
);

export const useAppointmentTemplates = () => {
  const toast = useToast();
  const { appointmentId, tenantId } = useAppointmentContext();
  const userId = useUserIdFromSession();

  const analytics = useAnalytics('appointment_templates');

  const data = useCompleteFragment({
    fragment: AppointmentTemplateFragment,
    key: {
      id: appointmentId,
    },
  });

  const [updateAppointmentTemplates] = useMutation(
    updateAppointmentTemplateMutation,
    {
      optimisticResponse: ({
        initialLlmOutputTemplateId,
        initialPromptTemplateId,
        id,
      }) => ({
        __typename: 'mutation_root',
        updateAppointmentsByPk: {
          __typename: 'Appointments' as const,
          id,
          initialPromptTemplateId: initialPromptTemplateId ?? null,
          initialLlmOutputTemplateId: initialLlmOutputTemplateId ?? null,
          llm_output_template: null,
        },
      }),
      onError: () => {
        toast.error('Unable to update appointment template');
      },
      onCompleted: (data) => {
        if (data.updateAppointmentsByPk) {
          analytics.capture('preferred_prompt_template_update', {
            tenantId,
            userId,
            appointmentId,
            promptTemplateId:
              data.updateAppointmentsByPk.initialPromptTemplateId,
            llmOutputTemplateId:
              data.updateAppointmentsByPk.initialLlmOutputTemplateId,
          });
        }
      },
    },
  );

  const [updateAppointmentPromptTemplate] = useMutation(
    updateAppointmentPromptTemplateMutation,
    {
      optimisticResponse: ({ initialPromptTemplateId, id }) => ({
        __typename: 'mutation_root',
        updateAppointmentsByPk: {
          __typename: 'Appointments' as const,
          id,
          initialPromptTemplateId: initialPromptTemplateId,
          llm_output_template: null,
        },
      }),
      onError: () => {
        toast.error('Unable to update appointment prompt template');
      },
    },
  );

  const [updateAppointmentOutputTemplate] = useMutation(
    updateAppointmentOutputTemplateMutation,
    {
      optimisticResponse: ({ initialLlmOutputTemplateId, id }) => ({
        __typename: 'mutation_root',
        updateAppointmentsByPk: {
          __typename: 'Appointments' as const,
          id,
          initialLlmOutputTemplateId: initialLlmOutputTemplateId,
          llm_output_template: null,
        },
      }),
      onError: () => {
        toast.error('Unable to update appointment output template');
      },
    },
  );

  return {
    initialPromptTemplateId: data?.initialPromptTemplateId,
    initialLlmOutputTemplateId: data?.initialLlmOutputTemplateId,
    updateAppointmentTemplates,
    updateAppointmentPromptTemplate,
    updateAppointmentOutputTemplate,
  };
};
